import React, { useCallback, useEffect, useRef, useState } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Box, useMediaQuery } from '@material-ui/core';
import { getMonthMarker, getTotalMonthsBetweenDates, today, yearRangesList } from '../utilities/commonUtilities/dateTools';
import { styleBreakMobile } from '../utilities/layouts/Globals';
import clsx from 'clsx';
import { SizeSelector } from '../utilities/layouts/SizeSelector';
import { getCustomColors } from '../../styles/CustomColors';
import { COLOR_WHITE } from '../utilities/MaterialWatchman';
import { CALENDAR_TABLE_WIDTH } from './CalendarDesktop';

const fontWeight = 700;
const minLastYearWidth = 70;
const Colors = getCustomColors();
const boxShadow = 'inset 1px 2px 3px 1px #e6e6e6';

const useStyles = makeStyles(theme => ({
    timelineProgressBar: {
        position: 'relative',
        width: '100%',
        minWidth: CALENDAR_TABLE_WIDTH,
        [styleBreakMobile(theme)]: {
            width: '100%',
            minWidth: 'auto',
        },
    },
    timelineSegment: {
        width: '50%',
        display: 'flex',
        alignItems: 'center',
        height: '100%',
    },
    timelineNumber: {
        color: theme.palette.primary.light,
        fontWeight,
        fontSize: theme.spacing(1.5),
    },
    timelineNumberUnmarked: {
        display: 'flex',
        height: '100%',
        alignItems: 'center',
        paddingRight: theme.spacing(2),
        borderTopRightRadius: theme.spacing(2.5),
        borderBottomRightRadius: theme.spacing(2.5),
        backgroundColor: theme.palette.primary.dark,
    },
    timelineNumberUnmarkedFirst: {
        borderTopLeftRadius: theme.spacing(2.5),
        borderBottomLeftRadius: theme.spacing(2.5),
        paddingLeft: theme.spacing(1.5),
    },
    timelineMarker: {
        width: theme.spacing(3.2),
        height: theme.spacing(3.2),
        border: `3px solid ${theme.palette.primary.main}`,
        color: theme.palette.primary.main,
        fontSize: theme.spacing(1.5),
        borderRadius: '50%',
        position: 'absolute',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: Colors.COLOR_WHITE,
        fontWeight,
        transform: 'translateX(-50%)',
        zIndex: 1,
    },
    timelineMarkerTwelfthMonth: {
        backgroundColor: theme.palette.primary.dark,
        color: theme.palette.primary.light,
        border: 'none',
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        height: theme.spacing(3.17),
        marginLeft: theme.spacing(1),
        zIndex: 0,
    },
    timelineMarkerNumber: {
        lineHeight: 'initial',
    },
    startEndContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: theme.spacing(0.5),
        [styleBreakMobile(theme)]: {
            marginBottom: theme.spacing(3),
            marginTop: theme.spacing(0.75),
        },
    },
    noBottomMarginStartEndContainer: {
        marginBottom: theme.spacing(0),
    },
    startDate: {
        fontSize: theme.spacing(1.5),
        '& strong': {
            marginRight: theme.spacing(1.3),
        },
        [styleBreakMobile(theme)]: getDatesMobileStyling(theme),
    },
    endDate: {
        fontSize: theme.spacing(1.5),
        textAlign: 'right',
        '& strong': {
            marginLeft: theme.spacing(1.3),
        },
        [styleBreakMobile(theme)]: getDatesMobileStyling(theme),
    },
    root: {
        height: theme.spacing(3.5),
        borderRadius: theme.spacing(2),
        marginTop: theme.spacing(2),
    },
    colorPrimary: {
        backgroundColor: 'white',
        boxShadow,
    },
    bar: {
        backgroundColor: theme.palette.primary.dark,
    },
    whiteColorPrimary: {
        backgroundColor: Colors.COLOR_WHITE,
        boxShadow,
    },
    defaultColorPrimary: {
        backgroundColor: theme.palette.background.default,
        boxShadow,
    },
    mobileProgressContainer: {
        boxShadow,
        backgroundColor: COLOR_WHITE,
        height: theme.spacing(3.5),
        borderRadius: theme.spacing(2),
        overflow: 'hidden',
        position: 'relative',
    },
    markedBar: {
        backgroundColor: theme.palette.primary.dark,
        borderTopLeftRadius: '25px',
        borderBottomLeftRadius: '25px',
        height: '25px',
        position: 'relative',
        maxWidth: '100%',
    },
    markedBarFull: {
        borderTopRightRadius: '25px',
        borderBottomRightRadius: '25px',
    },
    markedBarContainer: {
        backgroundColor: 'white',
        width: '100%',
        height: '27px',
        boxShadow: 'inset 1px 1px 2px 0 #ccc',
        borderRadius: '20px',
    },
}));

const getDatesMobileStyling = theme => {
    return {
        fontSize: theme.spacing(1.75),
        maxWidth: '70px',
        '& strong': {
            display: 'block',
        },
    };
};

function CalendarSegments({ endDate, startDate, progressPercent = null, isMobile = true }) {
    const theme = useTheme();
    const classes = useStyles();
    const smallmobileview = useMediaQuery(theme.breakpoints.down('sm'));
    let yearsList = yearRangesList(startDate, endDate);

    // on mobile view, the last year's width is 70px.
    let stageLastMarkerWidth = minLastYearWidth;
    let stageMarkerWidth = '50%';

    if (yearsList.length > 6) {
        stageLastMarkerWidth = minLastYearWidth * 2;
        // in smallest screens, 9-year progress should be narrower
        if (smallmobileview) {
            stageMarkerWidth = `${Math.floor(100 / yearsList.length)}%`;
            stageLastMarkerWidth = minLastYearWidth / 4;
        }
    }

    const flexSegments = [];
    for (let x = 0; x < yearsList.length; x++) {
        const width = x === yearsList.length - 1 && isMobile ? stageLastMarkerWidth : stageMarkerWidth;
        const isDateBetweenStartAndEnd = today.isBetween(yearsList[x].yearStartDate, yearsList[x].yearEndDate, '(]');
        const isMobileAndBarFilled = today.isAfter(yearsList[x].yearEndDate) && isMobile;
        const isMobileAndNumberFilled = isDateBetweenStartAndEnd && isMobile;
        const year = x + 1;

        flexSegments.push(
            <Box key={x} className={isMobileAndBarFilled ? classes.bar : ''} width={width} height="100%" display="flex" alignItems="center">
                <span
                    data-cy="timeline_number_label"
                    className={clsx(classes.timelineNumber, {
                        [classes.timelineNumberUnmarked]: isMobileAndNumberFilled,
                        [classes.timelineNumberUnmarkedFirst]: x === 0,
                    })}
                >
                    {year}
                </span>
            </Box>
        );
    }

    return flexSegments;
}

export function CalendarSegmentsDesktop({ endDate, startDate, containerWidth, isMobile = false }) {
    const classes = useStyles();
    let yearsList = yearRangesList(startDate, endDate);
    const totalMonths = getTotalMonthsBetweenDates(startDate, endDate);

    const flexSegments = [];
    for (let x = 0; x < yearsList.length; x++) {
        // const width = x === yearsList.length - 1 && isMobile ? stageLastMarkerWidth : stageMarkerWidth;
        let width = (containerWidth / totalMonths) * yearsList[x].monthsInYear;
        const isDateBetweenStartAndEnd = today.isBetween(yearsList[x].yearStartDate, yearsList[x].yearEndDate, '(]');
        const isMobileAndBarFilled = today.isAfter(yearsList[x].yearEndDate) && isMobile;
        const isMobileAndNumberFilled = isDateBetweenStartAndEnd && isMobile;
        const year = x + 1;

        if (width === Infinity || isNaN(width)) {
            width = 0;
        }

        flexSegments.push(
            <Box key={x} className={isMobileAndBarFilled ? classes.bar : ''} width={width} height="100%" display="flex" alignItems="center">
                <span
                    data-cy="timeline_number_label"
                    className={clsx(classes.timelineNumber, {
                        [classes.timelineNumberUnmarked]: isMobileAndNumberFilled,
                        [classes.timelineNumberUnmarkedFirst]: x === 0,
                    })}
                >
                    {year}
                </span>
            </Box>
        );
    }

    return flexSegments;
}

export const CalendarProgressDesktop = ({ endOfTreatmentDate, dateOfDiagnosis, tutorialCurrentPage }) => {
    const segmentsContainerRef = useRef(null);
    const [width, setWidth] = useState(0);

    function getRefWidth() {
        setWidth(segmentsContainerRef?.current?.offsetWidth);
    }

    useEffect(() => {
        getRefWidth();
    }, []);

    useEffect(() => {
        window.addEventListener('resize', getRefWidth);
        return () => {
            window.removeEventListener('resize', getRefWidth);
        };
    });

    return (
        <CalendarProgress endDate={endOfTreatmentDate} startDate={dateOfDiagnosis} tutorialCurrentPage={tutorialCurrentPage}>
            <Box position="absolute" zIndex={1} height="100%" width="100%" top={0} left={0}>
                <Box position="relative" height="100%" width="100%" display="flex" alignItems="center" ref={segmentsContainerRef}>
                    <CalendarSegmentsDesktop containerWidth={width} endDate={endOfTreatmentDate} startDate={dateOfDiagnosis} />
                </Box>
            </Box>
            <TimelineWithMarker start={dateOfDiagnosis} end={endOfTreatmentDate} />
        </CalendarProgress>
    );
};

export const TimelineWithMarker = ({ start, end, current = today }) => {
    const classes = useStyles();
    const totalMonths = getTotalMonthsBetweenDates(start, end);
    const monthsSinceDiagnosis = getTotalMonthsBetweenDates(start, current);
    const timelineRef = useRef(null);
    const markerContent = getMonthMarker(start, current);
    const [perMonthWidth, setPerMonthWidth] = useState(0);

    const getPerMonthWidth = useCallback(() => {
        const containerWidth = timelineRef?.current?.offsetWidth;
        if (!!containerWidth) {
            const pw = containerWidth / totalMonths;
            setPerMonthWidth(pw);
        }
    }, [totalMonths]);

    useEffect(() => {
        getPerMonthWidth();
    }, [getPerMonthWidth]);

    useEffect(() => {
        window.addEventListener('resize', getPerMonthWidth);
        return () => {
            window.removeEventListener('resize', getPerMonthWidth);
        };
    });

    let finalWidth = perMonthWidth * monthsSinceDiagnosis;
    if (finalWidth === 0) {
        finalWidth = perMonthWidth;
    }

    // if current month is at the end or beyond
    // (also handle if current date is before DoD)
    const finalMonthReached = monthsSinceDiagnosis >= totalMonths;

    return (
        <div ref={timelineRef} className={classes.markedBarContainer}>
            <div
                className={clsx(classes.markedBar, {
                    [classes.markedBarFull]: finalMonthReached,
                })}
                style={{ width: `${finalWidth}px` }}
            >
                {!finalMonthReached && !isNaN(markerContent) && markerContent > -1 && (
                    <div
                        data-cy="timeline_month_counter_label"
                        className={clsx(classes.timelineMarker, {
                            [classes.timelineMarkerTwelfthMonth]: markerContent === 0,
                        })}
                        style={{ position: 'absolute', left: '100%' }}
                    >
                        {markerContent === 0 ? '' : markerContent}
                    </div>
                )}
            </div>
        </div>
    );
};

export const CalendarProgressMobile = ({ endOfTreatmentDate, dateOfDiagnosis, timelineProgressBarClass, useDefaultColor, noBottomMarginStartEndContainer }) => {
    const classes = useStyles();
    return (
        <CalendarProgress
            endDate={endOfTreatmentDate}
            startDate={dateOfDiagnosis}
            timelineProgressBarClass={timelineProgressBarClass}
            noBottomMarginStartEndContainer={noBottomMarginStartEndContainer}
        >
            <Box className={classes.mobileProgressContainer}>
                <Box position="absolute" zIndex={1} height="100%" width="100%" top={0} left={0}>
                    <Box position="relative" height="100%" width="100%" display="flex" alignItems="center">
                        <CalendarSegments endDate={endOfTreatmentDate} startDate={dateOfDiagnosis} />
                    </Box>
                </Box>
            </Box>
        </CalendarProgress>
    );
};

export default function CalendarProgress({ endDate, startDate, children, timelineProgressBarClass, noBottomMarginStartEndContainer, tutorialCurrentPage }) {
    const classes = useStyles();
    const theme = useTheme();
    const mobileview = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <div
            className={timelineProgressBarClass ?? classes.timelineProgressBar}
            style={tutorialCurrentPage && { opacity: tutorialCurrentPage === 2 ? 1 : 0.4, minWidth: 'auto' }}
        >
            <Box position="relative">{children}</Box>
            <div
                className={clsx(classes.startEndContainer, {
                    [classes.noBottomMarginStartEndContainer]: noBottomMarginStartEndContainer,
                })}
            >
                <div className={classes.startDate}>
                    <strong>Start</strong> {startDate.format(!mobileview ? 'Do MMMM YYYY' : 'MMM YYYY')}
                </div>
                <div className={classes.endDate}>
                    <SizeSelector
                        mobile={
                            <>
                                <strong>End</strong>
                                {endDate.format(!mobileview ? 'MMMM YYYY' : 'MMM YYYY')}
                            </>
                        }
                        desktop={
                            <>
                                {endDate.format('MMMM YYYY')}
                                <strong>End</strong>
                            </>
                        }
                    />
                </div>
            </div>
        </div>
    );
}
